import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Search, ChevronLeft, ChevronRight, Eye, Edit, Trash2, X, Plus, Copy } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import ConfirmationModal from '@/components/ConfirmationModal';
import { Checkbox } from '@/components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthToken = () => sessionStorage.getItem('impersonation_token') || sessionStorage.getItem('token') || localStorage.getItem('token');

const ListCompanies = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get('page')) || 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [companyType, setCompanyType] = useState(searchParams.get('companyType') || '');
  const [deletedFilter, setDeletedFilter] = useState(searchParams.get('status') || 'active');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [expandedRow, setExpandedRow] = useState(searchParams.get('openCompanyId') || null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [originalCompany, setOriginalCompany] = useState(null); // Track original data
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Bulk selection state
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Modal states
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {},
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    showCancel: true
  });

  useEffect(() => {
    loadCompanies();
    loadStates();
  }, [pagination.page, search, companyType, deletedFilter]);

  // Sync filter state to URL query parameters
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (companyType) params.set('companyType', companyType);
    if (deletedFilter && deletedFilter !== 'active') params.set('status', deletedFilter);
    if (pagination.page > 1) params.set('page', String(pagination.page));
    if (expandedRow) params.set('openCompanyId', expandedRow);
    setSearchParams(params, { replace: true });
  }, [search, companyType, deletedFilter, pagination.page, expandedRow, setSearchParams]);

  // Deep link: auto-expand company when openCompanyId is in URL
  useEffect(() => {
    const openId = searchParams.get('openCompanyId');
    if (openId && !loading && companies.length > 0) {
      const found = companies.find(c => c.id === openId);
      if (found) {
        setExpandedRow(openId);
      }
    }
  }, [companies, loading]);

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (editingCompany) {
          handleCloseEdit();
        } else if (confirmModal.isOpen) {
          setConfirmModal({ ...confirmModal, isOpen: false });
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [editingCompany, confirmModal, originalCompany]);

  const loadStates = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/states`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStates(data.states || []);
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  const loadCities = async (stateCode) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/cities/${stateCode}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCities(data.cities || []);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search }),
        ...(companyType && { companyType }),
        filter: deletedFilter
      });

      const response = await fetch(`${BACKEND_URL}/api/admin/companies?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      setCompanies(data.companies || []);
      setPagination(prev => ({
        ...prev,
        ...data.pagination
      }));
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (type) => {
    setCompanyType(type);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Copy to clipboard function
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setConfirmModal({
        isOpen: true,
        title: 'Copied!',
        message: `${label} copied to clipboard`,
        type: 'success',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
      // Auto close after 1.5 seconds
      setTimeout(() => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }, 1500);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const handleViewToggle = (companyId) => {
    setExpandedRow(expandedRow === companyId ? null : companyId);
  };

  const handleEdit = (company) => {
    const companyData = {
      ...company,
      headquartersAddress: company.headquartersAddress || { street: '', state: '', city: '', zipCode: '' }
    };
    setEditingCompany(companyData);
    setOriginalCompany(JSON.parse(JSON.stringify(companyData))); // Deep clone
    setLogoFile(null);
    // Set logo preview - check Cloudinary first, then legacy
    if (company.logoCloudinary?.urls?.medium) {
      setLogoPreview(company.logoCloudinary.urls.medium);
    } else if (company.logo) {
      setLogoPreview(`${BACKEND_URL}${company.logo}?t=${company.updatedAt || company.createdAt || 'default'}`);
    } else {
      setLogoPreview(null);
    }
    if (company.headquartersAddress?.state) {
      loadCities(company.headquartersAddress.state);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const hasChanges = () => {
    if (!editingCompany || !originalCompany) return false;
    return JSON.stringify(editingCompany) !== JSON.stringify(originalCompany) || logoFile !== null;
  };

  const handleCloseEdit = () => {
    if (hasChanges()) {
      setConfirmModal({
        isOpen: true,
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to close? All changes will be lost.',
        type: 'warning',
        confirmText: 'Close Anyway',
        cancelText: 'Keep Editing',
        onConfirm: () => {
          setEditingCompany(null);
          setOriginalCompany(null);
          setCities([]);
          setLogoFile(null);
          setLogoPreview(null);
        }
      });
    } else {
      setEditingCompany(null);
      setOriginalCompany(null);
      setCities([]);
      setLogoFile(null);
      setLogoPreview(null);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    // Save original state for error rollback
    const originalCompanies = [...companies];
    
    try {
      // 1. Instant UI feedback - update local state immediately
      setCompanies(companies.map(c => c.id === editingCompany.id ? editingCompany : c));
      
      // 2. Send update request to backend
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/companies/${editingCompany.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editingCompany)
      });

      if (!response.ok) {
        // Revert on error
        setCompanies(originalCompanies);
        throw new Error('Failed to update company');
      }

      const updated = await response.json();
      
      // 3. Upload logo if a new file was selected
      if (logoFile) {
        try {
          const logoFormData = new FormData();
          logoFormData.append('file', logoFile);
          
          await fetch(`${BACKEND_URL}/api/admin/companies/${editingCompany.id}/logo`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: logoFormData
          });
          
          // Refresh company data to get updated logo URL
          const refreshResponse = await fetch(`${BACKEND_URL}/api/admin/companies/${editingCompany.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const refreshedCompany = await refreshResponse.json();
          setCompanies(companies.map(c => c.id === refreshedCompany.id ? refreshedCompany : c));
        } catch (logoError) {
          console.warn('Logo upload failed:', logoError);
        }
      } else {
        // Update with backend response to ensure data consistency
        setCompanies(companies.map(c => c.id === updated.id ? updated : c));
      }
      
      setEditingCompany(null);
      setOriginalCompany(null);
      setCities([]);
      setLogoFile(null);
      setLogoPreview(null);
      
      // Success message
      setConfirmModal({
        isOpen: true,
        title: 'Success',
        message: 'Company updated successfully!',
        type: 'success',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } catch (error) {
      console.error('Error updating company:', error);
      // Revert to original state on error
      setCompanies(originalCompanies);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to update company. Please try again.',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    }
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCompanies([]);
      setSelectAll(false);
    } else {
      setSelectedCompanies(companies.map(c => c.id));
      setSelectAll(true);
    }
  };

  const handleSelectCompany = (companyId) => {
    setSelectedCompanies(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedCompanies.length === 0) return;
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Multiple Companies',
      message: `Are you sure you want to delete ${selectedCompanies.length} company(ies)? This action cannot be undone.`,
      type: 'warning',
      confirmText: 'Delete All',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const token = getAuthToken();
          
          // Delete all selected companies
          await Promise.all(
            selectedCompanies.map(companyId =>
              fetch(`${BACKEND_URL}/api/admin/companies/${companyId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
              })
            )
          );
          
          // Clear selection
          setSelectedCompanies([]);
          setSelectAll(false);
          
          // Reload companies
          loadCompanies();
          
          setConfirmModal({
            isOpen: true,
            title: 'Success',
            message: `${selectedCompanies.length} company(ies) deleted successfully!`,
            type: 'success',
            confirmText: 'OK',
            showCancel: false,
            onConfirm: () => {}
          });
        } catch (error) {
          console.error('Error deleting companies:', error);
          loadCompanies();
          setConfirmModal({
            isOpen: true,
            title: 'Error',
            message: 'Failed to delete some companies. Please try again.',
            type: 'danger',
            confirmText: 'OK',
            showCancel: false,
            onConfirm: () => {}
          });
        }
      }
    });
  };

  const handleDeleteClick = (companyId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Company',
      message: 'Are you sure you want to delete this company?',
      type: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => handleDelete(companyId)
    });
  };

  const handleDelete = async (companyId) => {
    try {
      // 1. Instant UI feedback - immediately remove from list
      const newCompanies = companies.filter(c => c.id !== companyId);
      setCompanies(newCompanies);
      setDeleteConfirm(null);
      
      // 2. Update pagination manually
      const newTotal = pagination.total - 1;
      const newTotalPages = Math.ceil(newTotal / pagination.limit);
      
      setPagination(prev => ({
        ...prev,
        total: newTotal,
        totalPages: newTotalPages
      }));
      
      // 3. If current page becomes empty and not first page, go to previous page
      if (newCompanies.length === 0 && pagination.page > 1) {
        setPagination(prev => ({ ...prev, page: prev.page - 1 }));
        // Note: We're not reloading data, just adjusting page number
        // The previous page data will be loaded when pagination.page changes
      }
      
      // 4. Send delete request to backend
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/companies/${companyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete company');
      }
      
      setConfirmModal({
        isOpen: true,
        title: 'Success',
        message: 'Company deleted successfully!',
        type: 'success',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } catch (error) {
      console.error('Error deleting company:', error);
      // On error, reload to restore correct state
      loadCompanies();
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to delete company. Please try again.',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h2 className="text-3xl font-bold mb-2" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Companies</h2>
            <p className="text-gray-600">Manage all companies in the system</p>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex gap-4 mb-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search by company name..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Filter by Type */}
              <div className="w-64">
                <select
                  value={companyType}
                  onChange={(e) => handleFilterChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">All Company Types</option>
                  <option value="General Contractor">General Contractor</option>
                  <option value="Sub Contractor">Sub Contractor</option>
                </select>
              </div>

              {/* Deleted Filter */}
              <div className="w-48">
                <select
                  value={deletedFilter}
                  onChange={(e) => {
                    setDeletedFilter(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="deleted" className="text-red-600 font-semibold">Only Deleted</option>
                </select>
              </div>

              {/* Search Button */}
              <button
                onClick={handleSearch}
                className="text-white px-6 py-3 rounded-lg hover:opacity-90 transition-all"
                style={{ background: '#FF6600' }}
              >
                Search
              </button>

              {/* Clear Filters Button */}
              {(searchInput || companyType || deletedFilter !== 'active') && (
                <button
                  onClick={() => {
                    setSearchInput('');
                    setSearch('');
                    setCompanyType('');
                    setDeletedFilter('active');
                    setExpandedRow(null);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4" />
                  Clear Filters
                </button>
              )}
            </div>

            {/* Action Buttons Row */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => navigate('/admin/company-settings/create')}
                className="text-white px-6 py-2.5 rounded-lg hover:opacity-90 transition-all flex items-center gap-2"
                style={{ background: '#FF6600' }}
              >
                <Plus className="w-5 h-5" />
                Create Company
              </button>

              <button
                onClick={handleBulkDelete}
                disabled={selectedCompanies.length === 0 || deletedFilter === 'deleted'}
                className="bg-red-600 text-white px-6 py-2.5 rounded-lg hover:bg-red-700 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-5 h-5" />
                Delete Selected ({selectedCompanies.length})
              </button>
            </div>

            {/* Active Filters */}
            {(search || companyType || deletedFilter !== 'active') && (
              <div className="mt-4 flex gap-2">
                {search && (
                  <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">
                    Search: {search}
                    <button onClick={() => { setSearch(''); setSearchInput(''); }} className="hover:text-orange-900">×</button>
                  </span>
                )}
                {companyType && (
                  <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">
                    Type: {companyType}
                    <button onClick={() => setCompanyType('')} className="hover:text-orange-900">×</button>
                  </span>
                )}
                {deletedFilter === 'deleted' && (
                  <span className="inline-flex items-center gap-2 bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
                    Only Deleted
                    <button onClick={() => setDeletedFilter('active')} className="hover:text-red-900">×</button>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Companies Table */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="text-gray-500">Loading companies...</div>
              </div>
            ) : companies.length === 0 ? (
              <div className="p-12 text-center">
                <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No companies found</p>
                <button
                  onClick={() => navigate('/admin/company-settings/create')}
                  className="mt-4 text-blue-600 hover:text-blue-700"
                >
                  Create your first company
                </button>
              </div>
            ) : (
              <>
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 w-12">
                        <Checkbox
                          checked={selectAll}
                          onCheckedChange={handleSelectAll}
                          disabled={deletedFilter === 'deleted'}
                        />
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Logo</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Company Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Type</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Location</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Contact</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Users</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {companies.map((company) => {
                      const isSelected = selectedCompanies.includes(company.id);
                      
                      return (
                      <React.Fragment key={company.id}>
                        <tr className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => handleSelectCompany(company.id)}
                              disabled={deletedFilter === 'deleted'}
                            />
                          </td>
                          <td className="px-6 py-4">
                            {(company.logoCloudinary?.urls?.thumbnail || company.logo) ? (
                              <img
                                src={company.logoCloudinary?.urls?.thumbnail || `${BACKEND_URL}${company.logo}?t=${company.updatedAt || company.createdAt || 'default'}`}
                                alt={company.companyName}
                                className="w-12 h-12 object-contain rounded"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                <Building2 className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{company.companyName}</div>
                            {company.website && (
                              <div className="flex items-center gap-2">
                                <a
                                  href={company.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-700"
                                >
                                  {company.website}
                                </a>
                                <button
                                  onClick={() => copyToClipboard(company.website, 'Website')}
                                  className="text-gray-400 hover:text-gray-600 transition-colors"
                                  title="Copy website"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                              {company.companyType}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {company.headquartersAddress && (
                              <div className="text-sm text-gray-600">
                                {company.headquartersAddress.city && company.headquartersAddress.state && (
                                  <div>{company.headquartersAddress.city}, {company.headquartersAddress.state}</div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-600 space-y-1">
                              {company.email && (
                                <div className="flex items-center gap-2">
                                  {company.email}
                                  <button
                                    onClick={() => copyToClipboard(company.email, 'Email')}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Copy email"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                              {company.phone && <div>{company.phone}</div>}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-800 font-semibold text-sm">
                                {company.userCount || 0}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-3">
                              <button
                                onClick={() => handleViewToggle(company.id)}
                                className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                                title="View Details"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleEdit(company)}
                                disabled={deletedFilter === 'deleted'}
                                className={deletedFilter === 'deleted' 
                                  ? "text-gray-300 p-2 rounded-lg cursor-not-allowed" 
                                  : "text-orange-600 hover:text-orange-700 p-2 rounded-lg hover:bg-orange-50 transition-colors"}
                                title={deletedFilter === 'deleted' ? "Cannot edit deleted companies" : "Edit"}
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(company.id)}
                                disabled={deletedFilter === 'deleted'}
                                className={deletedFilter === 'deleted' 
                                  ? "text-gray-300 p-2 rounded-lg cursor-not-allowed" 
                                  : "text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"}
                                title={deletedFilter === 'deleted' ? "Cannot delete already deleted companies" : "Delete"}
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Expanded View Row */}
                        {expandedRow === company.id && (
                          <tr>
                            <td colSpan="8" className="px-6 py-6 bg-gray-50">
                              <div className="bg-white rounded-xl shadow-lg p-6">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-orange-200">
                                  <h3 className="text-xl font-bold" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                    Company Details
                                  </h3>
                                  <button 
                                    onClick={() => handleViewToggle(company.id)}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    <X className="w-5 h-5" />
                                  </button>
                                </div>

                                {/* Grid Layout */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                  {/* Company Info Card */}
                                  <div className="bg-blue-50 rounded-lg p-5 border-l-4 border-blue-500">
                                    <h4 className="font-bold text-blue-900 mb-4 flex items-center gap-2">
                                      <Building2 className="w-5 h-5" />
                                      Company Information
                                    </h4>
                                    <div className="space-y-3">
                                      <div>
                                        <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Company Name</label>
                                        <p className="text-sm text-gray-900 font-medium mt-1">{company.companyName}</p>
                                      </div>
                                      <div>
                                        <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Type</label>
                                        <p className="text-sm text-gray-900 mt-1">
                                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                            {company.companyType}
                                          </span>
                                        </p>
                                      </div>
                                      <div>
                                        <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Description</label>
                                        <p className="text-sm text-gray-700 mt-1 leading-relaxed">{company.description || 'No description provided'}</p>
                                      </div>
                                      <div>
                                        <label className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Active Users</label>
                                        <div className="flex items-center gap-2 mt-1">
                                          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-green-100 text-green-800 font-bold text-lg">
                                            {company.userCount || 0}
                                          </span>
                                          <span className="text-sm text-gray-600">users currently assigned to this company</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Contact Info Card */}
                                  <div className="bg-green-50 rounded-lg p-5 border-l-4 border-green-500">
                                    <h4 className="font-bold text-green-900 mb-4 flex items-center gap-2">
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                      </svg>
                                      Contact Information
                                    </h4>
                                    <div className="space-y-3">
                                      <div>
                                        <label className="text-xs font-semibold text-green-700 uppercase tracking-wide">Email</label>
                                        <div className="flex items-center gap-2 mt-1">
                                          <p className="text-sm text-gray-900">{company.email || 'N/A'}</p>
                                          {company.email && (
                                            <button
                                              onClick={() => copyToClipboard(company.email, 'Email')}
                                              className="text-gray-400 hover:text-gray-600 transition-colors"
                                              title="Copy email"
                                            >
                                              <Copy className="w-4 h-4" />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                      <div>
                                        <label className="text-xs font-semibold text-green-700 uppercase tracking-wide">Phone</label>
                                        <p className="text-sm text-gray-900 mt-1">{company.phone || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <label className="text-xs font-semibold text-green-700 uppercase tracking-wide">Fax</label>
                                        <p className="text-sm text-gray-900 mt-1">{company.fax || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <label className="text-xs font-semibold text-green-700 uppercase tracking-wide">Website</label>
                                        {company.website ? (
                                          <div className="flex items-center gap-2 mt-1">
                                            <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-700">
                                              {company.website}
                                            </a>
                                            <button
                                              onClick={() => copyToClipboard(company.website, 'Website')}
                                              className="text-gray-400 hover:text-gray-600 transition-colors"
                                              title="Copy website"
                                            >
                                              <Copy className="w-4 h-4" />
                                            </button>
                                          </div>
                                        ) : (
                                          <p className="text-sm text-gray-900 mt-1">N/A</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Address Info Card */}
                                  <div className="bg-orange-50 rounded-lg p-5 border-l-4 border-orange-500">
                                    <h4 className="font-bold text-orange-900 mb-4 flex items-center gap-2">
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                      </svg>
                                      Address Information
                                    </h4>
                                    <div className="space-y-3">
                                      {company.headquartersAddress ? (
                                        <>
                                          <div>
                                            <label className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Headquarters</label>
                                            <div className="text-sm text-gray-900 mt-1 space-y-1">
                                              {company.headquartersAddress.street && <p>{company.headquartersAddress.street}</p>}
                                              {(company.headquartersAddress.city || company.headquartersAddress.state) && (
                                                <p>{company.headquartersAddress.city}{company.headquartersAddress.city && company.headquartersAddress.state ? ', ' : ''}{company.headquartersAddress.state} {company.headquartersAddress.zipCode}</p>
                                              )}
                                            </div>
                                          </div>
                                        </>
                                      ) : (
                                        <p className="text-sm text-gray-500 italic">No headquarters address</p>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Logo Section - Full Width */}
                                {(company.logoCloudinary?.urls?.medium || company.logo) && (
                                  <div className="mt-6 pt-6 border-t border-gray-200">
                                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 block">Company Logo</label>
                                    <div className="bg-gray-50 rounded-lg p-4 inline-block">
                                      <img
                                        src={company.logoCloudinary?.urls?.medium || `${BACKEND_URL}${company.logo}?t=${company.updatedAt || company.createdAt || 'default'}`}
                                        alt={company.companyName}
                                        className="max-h-24 object-contain"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.total}</span> companies
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    {[...Array(pagination.totalPages)].map((_, idx) => {
                      const pageNum = idx + 1;
                      // Show first, last, current, and neighbors
                      if (
                        pageNum === 1 ||
                        pageNum === pagination.totalPages ||
                        (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                              pagination.page === pageNum
                                ? 'text-white'
                                : 'border border-gray-300 hover:bg-gray-100'
                            }`}
                            style={pagination.page === pageNum ? { background: '#FF6600' } : {}}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (
                        pageNum === pagination.page - 2 ||
                        pageNum === pagination.page + 2
                      ) {
                        return <span key={pageNum} className="px-2">...</span>;
                      }
                      return null;
                    })}

                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Edit Modal */}
          {editingCompany && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                  <h3 className="text-xl font-bold" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Edit Company
                  </h3>
                  <button onClick={handleCloseEdit} className="text-gray-400 hover:text-gray-600">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <form onSubmit={handleUpdate} className="p-6 space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                      <input
                        type="text"
                        required
                        value={editingCompany.companyName}
                        onChange={(e) => setEditingCompany({...editingCompany, companyName: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Type *</label>
                      <select
                        required
                        value={editingCompany.companyType}
                        onChange={(e) => setEditingCompany({...editingCompany, companyType: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="General Contractor">General Contractor</option>
                        <option value="Sub Contractor">Sub Contractor</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      rows={3}
                      value={editingCompany.description || ''}
                      onChange={(e) => setEditingCompany({...editingCompany, description: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  {/* Logo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Logo
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          className="hidden"
                          id="edit-logo-upload"
                        />
                        <label htmlFor="edit-logo-upload" className="cursor-pointer">
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-orange-500 transition-colors">
                            <p className="text-sm text-gray-600">Click to upload new logo</p>
                            <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 10MB</p>
                          </div>
                        </label>
                      </div>
                      {logoPreview && (
                        <div className="w-32 h-32 border-2 border-gray-200 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                          <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-700 mb-4">Headquarters Address</h4>
                    <div className="space-y-4">
                      <input
                        type="text"
                        placeholder="Street Address"
                        value={editingCompany.headquartersAddress?.street || ''}
                        onChange={(e) => setEditingCompany({
                          ...editingCompany,
                          headquartersAddress: {...editingCompany.headquartersAddress, street: e.target.value}
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <select
                          value={editingCompany.headquartersAddress?.state || ''}
                          onChange={(e) => {
                            const state = e.target.value;
                            setEditingCompany({
                              ...editingCompany,
                              headquartersAddress: {...editingCompany.headquartersAddress, state, city: ''}
                            });
                            if (state) loadCities(state);
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                          <option value="">Select state</option>
                          {states.map(state => (
                            <option key={state.code} value={state.code}>{state.name}</option>
                          ))}
                        </select>
                        <select
                          value={editingCompany.headquartersAddress?.city || ''}
                          onChange={(e) => setEditingCompany({
                            ...editingCompany,
                            headquartersAddress: {...editingCompany.headquartersAddress, city: e.target.value}
                          })}
                          disabled={!editingCompany.headquartersAddress?.state}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
                        >
                          <option value="">Select city</option>
                          {cities.map(city => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                        </select>
                      </div>
                      <input
                        type="text"
                        placeholder="ZIP Code"
                        value={editingCompany.headquartersAddress?.zipCode || ''}
                        onChange={(e) => setEditingCompany({
                          ...editingCompany,
                          headquartersAddress: {...editingCompany.headquartersAddress, zipCode: e.target.value}
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={editingCompany.email || ''}
                        onChange={(e) => setEditingCompany({...editingCompany, email: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                      <input
                        type="url"
                        value={editingCompany.website || ''}
                        onChange={(e) => setEditingCompany({...editingCompany, website: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={editingCompany.phone || ''}
                        onChange={(e) => setEditingCompany({...editingCompany, phone: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fax</label>
                      <input
                        type="tel"
                        value={editingCompany.fax || ''}
                        onChange={(e) => setEditingCompany({...editingCompany, fax: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 border-t">
                    <button
                      type="button"
                      onClick={handleCloseEdit}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-all"
                      style={{ background: '#FF6600' }}
                    >
                      Update Company
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Confirmation Modal */}
          <ConfirmationModal
            isOpen={confirmModal.isOpen}
            onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
            onConfirm={confirmModal.onConfirm}
            title={confirmModal.title}
            message={confirmModal.message}
            type={confirmModal.type}
            confirmText={confirmModal.confirmText}
            cancelText={confirmModal.cancelText}
            showCancel={confirmModal.showCancel}
          />
        </div>
      </div>
    </AdminLayout>
  );
};

export default ListCompanies;
